import { Injectable } from '@angular/core';
import { HelpersService } from './helpers';
import { LmapService } from './lmap';

@Injectable()
export class TestAssembleService {
  constructor(
    private readonly helperService: HelpersService,
    private readonly lmapService: LmapService
  ) {}

  // buildAgent
  // creates agent section for test submission - meant for use internally within testAssembler
  // params:
  //   tpInfo - instance of test point object
  //   configName - name of test configuration this agent will reference
  // returns:
  //   agent information for inclusion in overall configuration as a primary or secondary device or null if inputs are bad
  //
  //eslint-disable-next-line
  buildAgent(tpInfo: any, configName: string): any {
    let agentInfo = null;

    if (
      typeof tpInfo === 'object' &&
      typeof tpInfo.type === 'string' &&
      typeof tpInfo.name === 'string'
    ) {
      switch (tpInfo.type) {
        case 'map':
        case 'inventory':
        case 'GigaTestAgent':
        case 'manual': // for now they are the same TODO: update manual when it is better defined
          agentInfo = {};
          agentInfo.type = 'inventory'; //tpInfo.type;
          agentInfo.id = tpInfo.nameId;
          agentInfo.config = configName;
          break;
        default:
          // console.log('ERROR in buildAgent - unknown test point type: ' + tpInfo.type);
          break;
      }
    } else {
      //console.log('ERROR in buildAgent - bad test point type or test point name');
    }
    return agentInfo;
  }

  // buildPrimaryTestConfig
  // creates PRIMARY agent and test configuration for test submission
  // params:
  //   tpInfo - instance of test point (A) object
  //   testName - test type (y1564, twamp-initiator, etc)
  //   testConfig - all the test parameters
  //   testListData - an optional object that contains data that is necessary for display in test list later.
  //                  Note: It cannot contain a sub object named 'common' as that is reserved for the parent app to use
  // returns:
  //   overall configuration that can be sent to our controller or passed into addSecondaryTestConfig()
  //
  //eslint-disable-next-line
  buildPrimaryTestConfig = function (
    //eslint-disable-next-line
    tpInfo: any,
    testName: string,
    //eslint-disable-next-line
    testConfig: any,
    //eslint-disable-next-line
    testListData?: any
    //eslint-disable-next-line
  ): any {
    let configuration = null;

    if (
      typeof tpInfo === 'object' &&
      typeof testName === 'string' &&
      typeof testConfig === 'object'
    ) {
      let agentInfo = null;
      const configName = 'primaryConfig';

      agentInfo = this.buildAgent(tpInfo, configName);

      if (agentInfo != null) {
        configuration = {};
        configuration.agent = {};
        configuration.agent.primary = agentInfo;

        configuration.test = {}; // Controller currently requires this object to exist although it should be optional

        configuration.test['test-list-data'] = {};
        if (typeof testListData === 'object') {
          configuration.test['test-list-data'] = testListData;
        }

        // add some extra test point info for the test list
        configuration.test['test-list-data'].tpInfo = [];
        configuration.test['test-list-data'].tpInfo.push({
          type: 'primary',
          id: tpInfo.id,
          ip: tpInfo.ipaddress
        });

        const configInfo = {};
        configInfo[configName] = testConfig;
        configuration.config = [];
        configuration.config.push(configInfo);
      }
    } else {
      //   console.log('ERROR in buildPrimaryTestConfig - inputs are bad');
    }

    return configuration;
  };

  // addSecondaryTestConfig
  // adds SECONDARY agent and test configuration to an existing overall configuration
  // params:
  //   primaryTestObj - instance of overall test configuration (output from buildPrimaryTestConfig or this function)
  //   configName - name of the configuration that will be added and referenced by the agent
  //   tpInfo - instance of test point object that is used to create the agent
  //   testName - test type (y1564, twamp-initiator, etc)
  //   testConfig - all the test parameters
  // returns:
  //   overall configuration that can be sent to our controller or passed into addSecondaryTestConfig() again
  //
  //eslint-disable-next-line
  addSecondaryTestConfig(
    //eslint-disable-next-line
    primaryTestObj: any,
    configName: string,
    //eslint-disable-next-line
    tpInfo: any,
    testName: string,
    //eslint-disable-next-line
    testConfig: any
    //eslint-disable-next-line
  ): any {
    if (
      typeof primaryTestObj === 'object' &&
      typeof tpInfo === 'object' &&
      typeof testName === 'string' &&
      typeof testConfig === 'object' &&
      typeof configName === 'string'
    ) {
      if (
        typeof primaryTestObj.agent === 'object' &&
        primaryTestObj.config &&
        primaryTestObj.config instanceof Array
      ) {
        let agentInfo = null;

        agentInfo = this.buildAgent(tpInfo, configName);

        if (agentInfo != null) {
          if (primaryTestObj.agent.secondary) {
            primaryTestObj.agent.secondary.push(agentInfo);
          } else {
            primaryTestObj.agent.secondary = [];
            primaryTestObj.agent.secondary.push(agentInfo);
          }

          const configInfo = {};
          configInfo[configName] = testConfig;
          primaryTestObj.config.push(configInfo);

          // add some extra test point info for the test list
          if (primaryTestObj.test['test-list-data'].tpInfo) {
            primaryTestObj.test['test-list-data'].tpInfo.push({
              type: 'secondary',
              id: tpInfo.id,
              ip: tpInfo.ipaddress
            });
          }
        }
      } else {
        // console.log('ERROR in addSecondaryTestConfig - primary test configuration does not exist');
      }
    } else {
      //   console.log('ERROR in addSecondaryTestConfig - inputs are bad');
    }

    return primaryTestObj;
  }

  //eslint-disable-next-line
  updateFromY1564UnifiedYangConfig(y1564Obj: any, priorControllerConfig: any): any {
    const config = this.lmapService.getTestOptions('y1564', ['y1564'], priorControllerConfig);
    if (config && config.y1564) {
      const testOptions = JSON.parse(config.y1564);
      if (testOptions['y1564:y1564'] && testOptions['y1564:y1564']['y1564-parameters']) {
        const parms = testOptions['y1564:y1564']['y1564-parameters'];

        // probe API is ulr so since we don't have UI Data to tell us otherwise, go with ulr
        y1564Obj.JSONSetupData.bandwidthConvention = 'ulr';

        for (const x in parms) {
          switch (x) {
            case 'mode':
              y1564Obj.JSONSetupData.testMode = parms[x];
              break;
            case 'layer':
              switch (parms[x]) {
                case 'L2':
                  y1564Obj.JSONSetupData.layerToTest = '2';
                  break;
                case 'L3':
                  y1564Obj.JSONSetupData.layerToTest = '3';
                  break;
                case 'L4':
                  y1564Obj.JSONSetupData.layerToTest = '4';
                  break;
              }
              break;
            case 'unit-convention':
              if (parms[x] === 'l1-mbps') {
                y1564Obj.JSONSetupData.bandwidthConvention = 'ulr';
              } else {
                y1564Obj.JSONSetupData.bandwidthConvention = 'ir';
              }
              break;
            case 'run-config-test':
              y1564Obj.JSONSetupData.enableConfigTest = parms[x].toString();
              break;
            case 'step-duration':
              y1564Obj.JSONSetupData.configStepDuration = parms[x];
              break;
            case 'step-percents':
              if (parms[x].length) {
                for (let i = 0, len = parms[x].length; i < len; ++i) {
                  switch (i) {
                    case 0:
                      y1564Obj.JSONSetupData.configStep1 = parms[x][i];
                      break;
                    case 1:
                      y1564Obj.JSONSetupData.configStep2 = parms[x][i];
                      break;
                    case 2:
                      y1564Obj.JSONSetupData.configStep3 = parms[x][i];
                      break;
                    default:
                      break;
                  }
                }
              }
              break;
            case 'run-perf-test':
              y1564Obj.JSONSetupData.enablePerfTest = parms[x].toString();
              break;
            case 'perf-test-duration':
              y1564Obj.JSONSetupData.perfTestDuration = 'custom';
              y1564Obj.JSONSetupData.perfCustomDuration = this.helperService.convertSecondsToHMS(
                parms[x]
              );
              break;
            case 'tx-interface':
              y1564Obj.JSONSetupData.srcInterface = parms[x];
              break;
            case 'swap-addresses':
              y1564Obj.JSONSetupData.swapAddr = parms[x].toString();
              break;
            case 'frame-loss-interval':
              y1564Obj.JSONSetupData.frameLossInterval = parms[x];
              break;
            case 'ramp-up':
              y1564Obj.JSONSetupData.configReverseSteps = parms[x] == 'true' ? 'false' : 'true';
              break;
            case 'ip-address-type':
              y1564Obj.JSONSetupData.ipAddrType = parms[x];
              break;
            case 'suppress-loop-commands':
              y1564Obj.JSONSetupData.suppressLoopCommands = parms[x].toString();
              break;
            default:
              //   console.log('Configuration setting not handled: ' + x);
              break;
          }
        }
      }
      if (
        testOptions['y1564:y1564'] &&
        testOptions['y1564:y1564'].group &&
        testOptions['y1564:y1564'].group.length
      ) {
        const group = testOptions['y1564:y1564'].group[0];
        if (group.streams && group.streams.length) {
          const numStreams = group.streams.length;
          // now make connections with streams
          // this version of the test only supports one connection
          y1564Obj.JSONConnData = y1564Obj.createJSONConnectionData();
          const newConnection = y1564Obj.createJSONConnection(numStreams);
          newConnection.name = 'My Connection'; // only stream names are in our YANG model
          // start with defaults for source and dest
          newConnection.source.ipv4SrcType = 'preconfigured';
          if (
            y1564Obj.JSONSetupData.layerToTest === '3' ||
            y1564Obj.JSONSetupData.layerToTest === '4'
          ) {
            newConnection.destination.layer2DeviceType = 'preconfigured';
          }

          // set source and dest to custom values if applicable
          if (
            (y1564Obj.JSONSetupData.layerToTest === '3' ||
              y1564Obj.JSONSetupData.layerToTest === '4') &&
            y1564Obj.JSONSetupData.ipAddrType == 'ipv4'
          ) {
            if (testOptions['y1564:y1564']['engine-a-info']) {
              const engAParms = testOptions['y1564:y1564']['engine-a-info'];
              if (engAParms['test-ip'] && engAParms['test-ip'] === 'DHCP') {
                newConnection.source.ipv4SrcType = 'DHCP';
              } else if (
                engAParms['test-ip'] &&
                engAParms['test-subnet-mask'] &&
                engAParms['test-gateway']
              ) {
                newConnection.source.ipv4SrcType = 'manual';
                newConnection.destination.layer2DeviceType = 'ip';
                newConnection.source.ipv4SrcAddress = engAParms['test-ip'];
                newConnection.source.ipv4SrcSubnetMask = engAParms['test-subnet-mask'];
                newConnection.source.ipv4SrcGateway = engAParms['test-gateway'];
              }
            }
            if (testOptions['y1564:y1564']['engine-b-info']) {
              const engBParms = testOptions['y1564:y1564']['engine-b-info'];
              if (
                engBParms['test-ip'] &&
                engBParms['test-subnet-mask'] &&
                engBParms['test-gateway']
              ) {
                newConnection.destination.layer2DeviceType = 'ip';
                newConnection.destination.secondaryDestIpv4Addr = engBParms['test-ip'];
                newConnection.destination.secondaryDestIpv4Subnet = engBParms['test-subnet-mask'];
                newConnection.destination.secondaryDestIpv4Gateway = engBParms['test-gateway'];
              }
            }
          }

          for (let y = 0; y < numStreams; ++y) {
            for (const z in group.streams[y]) {
              let layerConfig = null;
              switch (z) {
                case 'name':
                  newConnection.streams[y].strmName = group.streams[y][z];
                  break;
                case 'cir-from-engine-a':
                  newConnection.streams[y]['strmSlaCir'] = group.streams[y][z].toString();
                  break;
                case 'eir-from-engine-a':
                  newConnection.streams[y]['strmSlaEir'] = group.streams[y][z].toString();
                  break;
                case 'run-policing-from-engine-a':
                  newConnection.streams[y]['strmSlaUseMaxIR'] = group.streams[y][z].toString();
                  break;
                case 'max-ir-from-engine-a':
                  newConnection.streams[y]['strmSlaMaxIR'] = group.streams[y][z].toString();
                  break;
                case 'cir-from-engine-b':
                  newConnection.streams[y]['strmSlaCir2'] = group.streams[y][z].toString();
                  break;
                case 'eir-from-engine-b':
                  newConnection.streams[y]['strmSlaEir2'] = group.streams[y][z].toString();
                  break;
                case 'run-policing-from-engine-b':
                  newConnection.streams[y]['strmSlaUseMaxIR2'] = group.streams[y][z].toString();
                  break;
                case 'max-ir-from-engine-b':
                  newConnection.streams[y]['strmSlaMaxIR2'] = group.streams[y][z].toString();
                  break;
                case 'frame-loss-ratio-threshold':
                  newConnection.streams[y]['strmSlaFrameLoss'] = group.streams[y][z].toString();
                  break;
                case 'enable-frame-delay':
                  newConnection.streams[y]['strmEnableFTD'] = group.streams[y][z].toString();
                  break;
                case 'frame-delay-threshold':
                  newConnection.streams[y]['strmSlaFrameDelay'] = group.streams[y][z].toString();
                  break;
                case 'fail-on-OOS':
                  newConnection.streams[y]['strmFailOnOOS'] = group.streams[y][z].toString();
                  break;
                case 'latency-FDV-maximum':
                  newConnection.streams[y]['strmSlaFdv'] = group.streams[y][z].toString();
                  break;
                case 'destination-type':
                  if (group.streams[y][z].toString() === 'Acterna2802') {
                    newConnection.destination.configureLoopback = 'true';
                  } else {
                    newConnection.destination.configureLoopback = 'false';
                  }
                  break;
                case 'destination-ip':
                  newConnection.destination.ipv4DestAddr = group.streams[y][z].toString();
                  break;
                case 'overhead-byte-count':
                  newConnection.network.overheadByteCount = group.streams[y][z].toString();
                  break;
                case 'cbs-size':
                  newConnection.streams[y]['strmSlaCbs'] = group.streams[y][z].toString();
                  break;
                case 'cbs-kilo-size':
                  newConnection.streams[y]['strmSlaKBSize'] = group.streams[y][z]
                    .split('cbs')
                    .join('');
                  break;
                case 'cbs-number-of-reps':
                  newConnection.streams[y]['strmSlaCbsRep'] = group.streams[y][z].toString();
                  break;
                case 'cbs-remote-line-rate':
                  newConnection.streams[y]['strmSlaAccessRate'] = group.streams[y][z].toString();
                  break;
                case 'l2-config':
                  layerConfig = group.streams[y][z];
                  if (layerConfig['source-mac']) {
                    newConnection.source.srcMac = layerConfig['source-mac'];
                    newConnection.source.useFactoryDefaultMac = 'false';
                  }
                  if (layerConfig['destination-mac']) {
                    newConnection.destination.destMac = layerConfig['destination-mac'];
                  }
                  if (layerConfig['remote-tag-count']) {
                    newConnection.destination.remoteTagCount = layerConfig['remote-tag-count'];
                    if (newConnection.destination.remoteVlanTagType) {
                      let tagType = 'no-vlan';
                      switch (newConnection.destination.remoteTagCount) {
                        case 1:
                          tagType = '1-vlan';
                          break;
                        case 2:
                          tagType = '2-vlan';
                          break;
                      }
                      newConnection.destination.remoteVlanTagType = tagType;
                    }
                  }
                  if (layerConfig['vlan-id']) {
                    newConnection.network.sVlan = layerConfig['vlan-id'];
                  }
                  if (layerConfig['ether-type']) {
                    newConnection.network.sVlanEtherType = layerConfig['ether-type'];
                  }
                  if (layerConfig['vlan-priority']) {
                    newConnection.streams[y]['strmSVlanPri'] =
                      layerConfig['vlan-priority'].toString();
                  }
                  if (layerConfig['inner-vlan-id']) {
                    newConnection.network.cVlan = layerConfig['inner-vlan-id'];
                  }
                  if (layerConfig['inner-ether-type']) {
                    newConnection.network.cVlanEtherType = layerConfig['inner-ether-type'];
                  }
                  if (layerConfig['inner-vlan-priority']) {
                    newConnection.streams[y]['strmCVlanPri'] =
                      layerConfig['inner-vlan-priority'].toString();
                  }
                  if (layerConfig['filter-on-priority']) {
                    newConnection.streams[y]['strmVerifyLayer2Pri'] =
                      layerConfig['filter-on-priority'].toString();
                  }
                  if (layerConfig['arp-mode']) {
                    newConnection.destination.useArp = layerConfig['arp-mode'];
                  }
                  if (layerConfig['sct-frame-size-list']) {
                    this.setFrameSizes(
                      layerConfig['sct-frame-size-list'],
                      newConnection.streams[y],
                      'strmFrameConf'
                    );
                  }
                  if (layerConfig['spt-frame-size-list']) {
                    this.setFrameSizes(
                      layerConfig['spt-frame-size-list'],
                      newConnection.streams[y],
                      'strmFramePerf'
                    );
                  }
                  if (layerConfig['frame_type']) {
                    newConnection.streams[y]['strmFrameType'] = layerConfig['frame_type'];
                  }
                  if (layerConfig['payload']) {
                    newConnection.streams[y]['strmPayloadType'] = layerConfig['payload'];
                  }
                  break;
                case 'l3-ipv4-config':
                  layerConfig = group.streams[y][z];
                  if (layerConfig['destination-ip']) {
                    newConnection.destination.ipv4DestAddr = layerConfig['destination-ip'];
                    if (newConnection.destination.defaultIpv4Addr) {
                      newConnection.destination.defaultIpv4Addr = layerConfig['destination-ip'];
                    }
                  }
                  if (
                    typeof layerConfig['tos'] === 'number' ||
                    typeof layerConfig['tos'] === 'string'
                  ) {
                    newConnection.streams[y]['strmIpv4ToSDiffServ'] = 'tos';
                    newConnection.streams[y]['strmIpv4TosVal'] = this.helperService.formatTOS(
                      layerConfig['tos'].toString()
                    );
                  }
                  if (layerConfig['ttl']) {
                    newConnection.streams[y]['strmIpv4TTL'] = layerConfig['ttl'].toString();
                  }
                  if (layerConfig['payload']) {
                    newConnection.streams[y]['strmPayloadType'] = layerConfig['payload'];
                  }
                  break;
                case 'l3-ipv6-config':
                  layerConfig = group.streams[y][z];
                  if (layerConfig['source-ipv6-address']) {
                    if (y1564Obj.JSONSetupData.layerToTest === '2') {
                      newConnection.source.ipv6SrcAddress = layerConfig['source-ipv6-address'];
                    } else {
                      newConnection.source.ipv6SrcIpAddress = layerConfig['source-ipv6-address'];
                    }
                  }
                  if (layerConfig['dest-ipv6-address']) {
                    if (y1564Obj.JSONSetupData.layerToTest === '2') {
                      newConnection.destination.ipv6destinationIPaddress =
                        layerConfig['dest-ipv6-address'];
                    } else {
                      newConnection.destination.layer3Ipv6destinationIPaddress =
                        layerConfig['dest-ipv6-address'];
                    }
                  }
                  if (layerConfig['src-subnet-prefix-length']) {
                    newConnection.source.ipv6SrcSubnet = layerConfig['src-subnet-prefix-length'];
                  }
                  if (layerConfig['src-gateway-ipv6-address']) {
                    newConnection.source.ipv6SrcGateway = layerConfig['src-gateway-ipv6-address'];
                  }
                  if (layerConfig['traffic-class']) {
                    newConnection.streams[y]['strmIpv6TrafficClass'] = layerConfig['traffic-class'];
                  }
                  if (layerConfig['hop-limit']) {
                    newConnection.streams[y]['strmIpv6HopLimit'] = layerConfig['hop-limit'];
                  }
                  if (layerConfig['flow-label']) {
                    newConnection.streams[y]['strmIpv6FlowLabel'] = layerConfig['flow-label'];
                    newConnection.streams[y]['strmIpv6FlowLabelType'] = 'User Defined';
                  }
                  break;
                default:
                  //   console.log('Stream configuration setting not handled: ' + z);
                  break;
              }
            }
          }
          y1564Obj.JSONConnData.connections.push(newConnection);
        }
      }
    }
  }
  //eslint-disable-next-line
  setFrameSizes(list: any, stream: any, item: any): any {
    const frames = list
      .replace(/^[, ]+|[, ]+$|[, ]+/g, ' ')
      .trim()
      .split(' ');
    if (frames.length) {
      if (frames[0] === 'EMIX') {
        if (item == 'strmFrameConf') {
          stream[item] = frames.join(',');
        } else {
          stream[item] = 'EMIX';
        }
        frames.shift();
        stream['strmEmixList'] = frames.join(',');
      } else if (frames[0].indexOf('custom:') >= 0) {
        stream[item] = frames[0];
        stream['strmCustomFrameSize'] = frames[0].split('custom:').join('');
      } else {
        const commonFrames = ['66', '128', '256', '512', '1024', '1280', '1518', '9000'];
        if (commonFrames.includes(frames[0])) {
          stream[item] = frames[0];
        } else {
          stream[item] = 'custom:' + frames[0];
          stream['strmCustomFrameSize'] = frames[0];
        }
      }
    }
  }
}
